import { Fragment, useRef } from 'react';

import { useHelpfindUploady } from '@/hooks/useHelpfindUploady';
import useToggleState from '@/hooks/useToggleState';

import Cloud from '@/assets/cloud.svg?react';
import { validFileTypes } from '@/components/helpers/isValidFile';
import Button from '@/components/ui/Button';
import { Hint } from '@/components/ui/Hint';
import { API_URL, WebsiteType } from '@/consts';
import cn from '@/utils/cn';

import { CaseCardPageData } from '../content.types';
import Dialog from './Dialog';
import FileItem from './FileItem';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { useLingui } from '@lingui/react';
import ChunkedUploady from '@rpldy/chunked-uploady';
import { retryEnhancer } from '@rpldy/retry-hooks';
import UploadDropZone from '@rpldy/upload-drop-zone';
import { useParams } from 'react-router-dom';

type Content = CaseCardPageData;

export interface UploaderProps {
  btnText?: string | React.ReactNode;
  btnIcon?: React.ReactNode;
  btnDescription?: string | React.ReactNode;
  title: string;
  required?: boolean;
  inputFieldName?: string;
  triggerOnMount?: boolean;
  content: Content;
  variant?: WebsiteType;
}

const handleDataLayer = () => {
  window?.dataLayer?.push({
    event: 'document_sent',
  });
};

export const Uploader = (props: UploaderProps) => {
  const { id: hash } = useParams<{ id: string }>();

  return (
    <ChunkedUploady
      method="POST"
      destination={{
        url: `${API_URL}/api/case-card/${hash}/attachments`,
        headers: {
          accept: 'application/json',
          pragma: 'no-cache',
          cacheControl: 'no-cache',
        },
        filesParamName: 'file',
      }}
      concurrent
      maxConcurrent={3}
      chunkSize={1048576}
      inputFieldName={props.inputFieldName || 'files'}
      multiple
      autoUpload={false}
      accept={validFileTypes.join(',')}
      enhancer={retryEnhancer}
      fileInputId="uploader"
      data-test="uploader"
    >
      <UploaderPrimitive {...props} />
    </ChunkedUploady>
  );
};
const UploaderPrimitive = ({
  title,
  btnText,
  btnDescription,
  btnIcon = <Cloud width={36} height={36} className="text-blue-500" />,
  required = false,
  content,
  variant = WebsiteType.HELPFIND,
}: UploaderProps) => {
  const ref = useRef<HTMLInputElement>(null);
  const { i18n } = useLingui();

  const {
    files,
    handleUpdatePassword,
    handleDeleteFile,
    handleRetry,
    ButtonFile,
    error,
    warning,
    clearFiles,
    clearError,
    clearWarning,
  } = useHelpfindUploady({
    defaultValue: [],
    multiple: true,
    onSuccess: handleDataLayer,
  });
  const [parent] = useAutoAnimate();
  const [isDialogOpen, toggleDialog] = useToggleState(false);

  const handleOpenModal = () => {
    clearFiles();
    clearError();
    clearWarning();
    toggleDialog();
  };

  const handleCloseModal = () => {
    toggleDialog();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <div className="mt-4 rounded-lg bg-gray-100 px-3.5 py-4 lg:mt-5 lg:p-6">
        <div className={cn('m-0.5 flex flex-col lg:m-0')}>
          <div className="flex items-center justify-between pb-3 lg:pb-4">
            <h4 className="font-semibold">
              {title}
              {required && (
                <span data-testid="required">
                  {' '}
                  <span className="text-red-400">{i18n.t('wymagane')}</span>
                </span>
              )}
            </h4>
          </div>
          <UploadDropZone
            className="group z-10 w-full cursor-pointer"
            extraProps={{
              onClick: () => {
                if (ref.current) ref.current.click();
              },
            }}
          >
            <button
              type="button"
              className={cn(
                'relative',
                'custom-dash flex w-full appearance-none flex-col items-center gap-2 rounded-[10px] border-blue-600 bg-blue-100 p-6 outline outline-1 -outline-offset-1 outline-white/0 transition-all duration-200 group-hover:outline-blue-600'
              )}
            >
              {btnIcon}
              <h4 className="font-semibold text-blue-600">
                {btnText || (
                  <>
                    <span>{i18n.t('Wgraj plik z dysku')}</span>{' '}
                    <span className="hidden md:inline-block">
                      {i18n.t('lub przeciągnij go tutaj')}
                    </span>
                  </>
                )}
              </h4>
              <p className="body-small font-normal text-dark-gray-400">
                {btnDescription ||
                  i18n.t(
                    'Akceptowane pliki: .jpg, .pdf, .jfif, .png, .heic, .zip, oraz .7zip.'
                  )}
              </p>
              <ButtonFile
                className="absolute inset-0 size-full"
                text={' '}
                ref={ref}
              />
            </button>
          </UploadDropZone>
          {files.length > 0 && (
            <div
              className={cn(
                'flex flex-col gap-3 lg:gap-4',
                files.length > 0 && 'mt-4'
              )}
              ref={parent}
            >
              {files.map((item) => (
                <Fragment key={item.name + item.size}>
                  <FileItem
                    item={{ ...item, locked: false }}
                    handleDeleteFile={handleDeleteFile}
                    handleRetry={handleRetry}
                    handleUpdatePassword={handleUpdatePassword}
                  />
                </Fragment>
              ))}
            </div>
          )}
          {error && (
            <Hint
              color="red"
              className={cn('mb-0 mt-4', files.length > 0 && 'mt-2')}
            >
              {error}
            </Hint>
          )}
          {warning && (
            <Hint
              color="yellow"
              className={cn('mb-0 mt-4', files.length > 0 && 'mt-2')}
            >
              {warning}
            </Hint>
          )}
        </div>
      </div>
      <Button
        onClick={handleOpenModal}
        className="mt-4 w-full lg:mt-5 lg:w-full"
        disabled={!files.find((f) => f.status === 'finished')}
        colors={
          content.button.color ??
          (variant === WebsiteType.DEEPRY ? 'yellow' : 'blue')
        }
        size={variant === WebsiteType.DEEPRY ? 'deepDryLarge' : 'large'}
        intent={variant === WebsiteType.DEEPRY ? 'deepDry' : 'primary'}
      >
        {content.button.text}
      </Button>
      {isDialogOpen && (
        <Dialog
          {...content.dialog}
          onClose={handleCloseModal}
          variant={variant}
        />
      )}
    </>
  );
};
